// Core
import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// Assets
import { apiService } from 'services/ApiService';

import MarketPriceContent from './components/MarketPriceContent';
import { centsToPrice, priceToCents } from '../../../services/Utils';
import useFormPageRedirect from '../../../hooks/useFormPageRedirect';

const marketPriceSchema = z.object({
  name: z.string().min(1),
  code: z.string().min(1),
  price: z.coerce.number(),
  stationId: z.number().int().nullable().optional(),
  isActive: z.boolean(),
});

const MarketPriceEdit = () => {
  const history = useHistory();
  const { id } = useParams();

  const [errorCard, setErrorCard] = useState();

  const form = useForm({
    resolver: zodResolver(marketPriceSchema),
    defaultValues: {
      name: '',
      code: '',
      price: 0,
      isActive: false,
      stationId: undefined,
    },
  });

  const { reset, handleSubmit } = form;

  const { canRead } = useFormPageRedirect({
    id,
    createPermissionKey: 'POST:/admin/market-prices',
    readPermissionKey: 'GET:/admin/market-prices/:id',
    redirectPath: '/market-prices',
  });

  const { isLoading, isError } = useQuery({
    queryKey: ['market-prices', id, canRead],
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnError: false,
    queryFn: async () => {
      if (id === 'new') {
        reset({
          name: '',
          code: '',
          price: 0,
          isActive: false,
        });
        return true;
      }
      if (canRead) {
        const response = await apiService.getMarketPrice({ id });
        reset({
          ...response.data,
          price: priceToCents(response?.data?.price || 0),
        });
      }

      return true;
    },
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      const payload = { ...data, price: centsToPrice(data?.price || 0) };
      if (id === 'new') {
        return apiService.createMarketPrice(payload);
      } else {
        return apiService.updateMarketPrice({ id, ...payload });
      }
    },
    onSuccess: (res) => {
      if (
        res.data === false ||
        !(res.statusCode >= 200 && res.statusCode < 300)
      ) {
        setErrorCard(res.message);
        return;
      }
      history.push('/market-prices');
    },
    onError: () => {
      setErrorCard('Something went wrong.');
    },
  });

  const onSubmit = (values) => {
    mutation.mutate(values);
  };

  const onError = (errors) => {
    console.log('Validation errors:', errors);

    const messages = Object.values(errors)
      .map((err) => err?.message)
      .filter(Boolean)
      .join('. ');

    setErrorCard(messages);
  };

  const handleDelete = useCallback(async () => {
    await apiService.deleteMarketPrice({ id });
    reset({
      name: '',
      code: '',
      price: 0,
      isActive: false,
    });
    history.push('/market-prices');
  }, [history, id, reset]);

  return isError ? (
    <div>Not Found</div>
  ) : isLoading ? (
    <div>Loading...</div>
  ) : (
    <MarketPriceContent
      handleDelete={handleDelete}
      isNew={id === 'new'}
      form={form}
      onSubmit={handleSubmit(onSubmit, onError)}
      isSubmitting={mutation.isLoading}
      errorCard={errorCard}
    />
  );
};

export default MarketPriceEdit;
